import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../config";

interface Props {
  mode: "signin" | "signup";
  redirectTo: string;
}

export default function EmailAuth({ mode, redirectTo }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Password requirements
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const passwordValid = hasMinLength && hasUppercase && hasLowercase && hasNumber;
  const confirmPasswordValid = mode === "signin" || password === confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!emailValid) {
      setError("Enter a valid email address");
      return;
    }
    if (mode === "signup") {
      if (!hasMinLength) {
        setError("Password must be at least 8 characters");
        return;
      }
      if (!hasUppercase) {
        setError("Password must contain an uppercase letter");
        return;
      }
      if (!hasLowercase) {
        setError("Password must contain a lowercase letter");
        return;
      }
      if (!hasNumber) {
        setError("Password must contain a number");
        return;
      }
      if (!confirmPasswordValid) {
        setError("Passwords do not match");
        return;
      }
    } else if (!password) {
      setError("Password required");
      return;
    }

    setLoading(true);
    try {
      const endpoint = mode === "signup" ? "/api/auth/email-signup" : "/api/auth/email-signin";
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mode === "signup"
            ? { email, password, confirmPassword }
            : { email, password }
        ),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Authentication failed");
        return;
      }
      login(data.jwtToken);
      navigate(next || redirectTo);
    } catch {
      setError("Connection failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: 10,
    margin: "10px 0",
    boxSizing: "border-box",
    fontSize: 16,
  };

  const RequirementCheck: React.FC<{ met: boolean; label: string }> = ({ met, label }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: met ? "#10b981" : "#9ca3af", marginBottom: 6 }}>
      <span style={{ fontWeight: 700 }}>{met ? "✓" : "○"}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <form onSubmit={handleSubmit}>
      {error && <p style={{ color: "#ef4444", marginBottom: 16, fontSize: 14 }}>{error}</p>}
      <input
        type="email"
        placeholder="Email"
        value={email}
        onChange={(e) => { setEmail(e.target.value); setError(null); }}
        autoFocus
        style={inputStyle}
      />
      <input
        type="password"
        placeholder="Password"
        value={password}
        onChange={(e) => { setPassword(e.target.value); setError(null); }}
        style={inputStyle}
      />
      {mode === "signup" && password && (
        <div style={{ padding: "10px 12px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 4, marginBottom: 10 }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#374151" }}>Password must contain:</p>
          <RequirementCheck met={hasMinLength} label="At least 8 characters" />
          <RequirementCheck met={hasUppercase} label="An uppercase letter (A-Z)" />
          <RequirementCheck met={hasLowercase} label="A lowercase letter (a-z)" />
          <RequirementCheck met={hasNumber} label="A number (0-9)" />
        </div>
      )}
      {mode === "signup" && (
        <input
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
          style={inputStyle}
        />
      )}
      <button
        type="submit"
        disabled={loading || !emailValid || (mode === "signup" ? !passwordValid || !confirmPasswordValid : !password)}
        style={{ padding: "10px 20px", width: "100%" }}
      >
        {loading ? (mode === "signup" ? "Creating account..." : "Signing in...") : (mode === "signup" ? "Create Account" : "Sign In")}
      </button>
    </form>
  );
}
